'use client';

import { useState, useEffect } from "react";
import axios from "axios";
import Link from "next/link";
import { AlbumItem } from "@/types/gallery";
import { ApiResponse } from "@/types/api";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faChevronRight, faImages, faArrowRight, faChevronLeft
} from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from '@/context/LanguageContext';

import { useUnit } from "@/context/UnitContext";

// ...

export default function GaleriContent() {
    const { unitId, unitQueryParams } = useUnit();
    const { language } = useTranslation();
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
    const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
    const [albums, setAlbums] = useState<AlbumItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0); // 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const limit = 9; // Grid 3x3

    useEffect(() => {
        const fetchAlbums = async () => {
            if (!unitId && !unitQueryParams) return;
            
            setLoading(true);
            try {
                const params = unitQueryParams || (unitId ? `unit_id=${unitId}` : '');
                const response = await axios.post<ApiResponse<AlbumItem[]>>('/api/doServices', {
                    method: 'GET',
                    endpoint: `web/album?limit=${limit}&page=${currentPage + 1}&${params}&order=created_at DESC`
                });

                const apiData = response.data;

                if (apiData && apiData.data) {
                    setAlbums(apiData.data);
                    const pages = Number(apiData.metadata?.total_page) || 0;
                    setTotalPages(pages);
                }
            } catch (error: any) {
                console.error('❌ Error fetching albums:', error.response?.data || error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAlbums();
    }, [currentPage, unitId, unitQueryParams]);

    // Pagination helpers
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible);

        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible);
        }

        if (currentPage > 0) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            );
        }

        for (let i = start; i < end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${i === currentPage
                        ? 'bg-unmulGreen text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    {i + 1}
                </button>
            );
        }

        if (end < totalPages) {
            pages.push(
                <span key="ellipsis" className="w-10 h-10 flex items-center justify-center text-gray-400">
                    ...
                </span>
            );
            pages.push(
                <button
                    key={totalPages - 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    {totalPages}
                </button>
            );
        }

        if (currentPage < totalPages - 1) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            );
        }

        return pages;
    };

    return (
        <>
            {/* Hero Header - Creative Theme Colored */}
            <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
                {/* SVG Ornaments Background */}
                <div className="absolute inset-0 opacity-10">
                    <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                    </svg>
                    {/* Dots Pattern */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[url('/img/pattern-dots.svg')] opacity-20 transform rotate-45"></div>
                    {/* Circular Blurs */}
                    <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
                        <Link href="/" className="hover:text-white transition"><FontAwesomeIcon icon={faHome} /></Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-unmulYellow font-medium">Galeri</span>
                    </nav>

                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" data-aos="fade-up">
                        {language === 'en' ? 'University' : 'Galeri'} <span className="text-unmulYellow">{language === 'en' ? 'Gallery' : 'Universitas'}</span>
                    </h1>
                    <p className="text-white/80 text-lg max-w-2xl" data-aos="fade-up" data-aos-delay="100">
                        {language === 'en'
                          ? `Documentation of activities and important moments at ${siteNameEnglish}`
                          : `Dokumentasi kegiatan dan momen penting di ${siteName}`
                        }
                    </p>
                </div>
            </section>

            {/* Album Grid */}
            <section className="py-12 bg-unmulPale">
                <div className="container mx-auto px-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
                        </div>
                    ) : albums.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <FontAwesomeIcon icon={faImages} className="text-4xl text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg">Tidak ada album galeri saat ini.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {albums.map((album, index) => (
                                    <Link
                                        key={album.id}
                                        href={`/galeri/${album.slug}`}
                                        className="group"
                                        data-aos="fade-up"
                                        data-aos-delay={index * 50}
                                    >
                                        <article className="bg-white rounded-xl shadow-sm hover:shadow-xl transition border border-gray-100 overflow-hidden h-full flex flex-col">
                                            {/* Cover Image */}
                                            <div className="h-56 overflow-hidden bg-gray-100">
                                                <img
                                                    src={`/api/doServicesProxy?endpoint=web/file/album/${album.id}&isImage=true`}
                                                    alt={album.title_idn}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = 'https://via.placeholder.com/600x400/006633/FFCC00?text=Album';
                                                    }}
                                                />
                                            </div>
                                            {/* Content */}
                                            <div className="p-6 flex-1 flex flex-col">
                                                <h3 className="text-lg font-bold text-gray-800 mb-3 group-hover:text-unmulGreen transition line-clamp-2">
                                                    {album.title_idn || album.title_en}
                                                </h3>
                                                <div className="mt-auto flex items-center justify-between">
                                                    <span className="text-sm text-gray-400">
                                                        <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                                                        {new Date(album.created_at).toLocaleDateString('id-ID', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                    <span className="text-unmulGreen font-bold text-sm inline-flex items-center gap-1">
                                                        Lihat Album
                                                        <FontAwesomeIcon icon={faArrowRight} className="text-xs group-hover:translate-x-1 transition" />
                                                    </span>
                                                </div>
                                            </div>
                                        </article>
                                    </Link>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center gap-2 mt-12">
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
